import { infoIcon } from '../styles';
import { IconLegendTooltipComponent } from './custom-renderer/icon-legend-tooltip-component';
import { CreditProfileLegendTooltipComponent } from './custom-renderer/credit-profile-legend-tooltip-component';
import { CONTRACT_TYPE } from './enums/generic.enum';
import { LOCAL_STORAGE_GRID_KEY } from './grid-delegate';

export function getGroupColumnSameObject(params: any, column: string): any {
  if (params.data && params.data[column]) {
    return params.data[column];
  }
  if (params.node && params.node.allLeafChildren) {
    return params.node.allLeafChildren[0].data[column];
  }
  if (params.node) {
    return params.node.data;
  }

  return '';
}

export function getGroupColumnSameObjectForVolume(params: any, column: string): any {
  if (params.data && params.data[column]) {
    return params.data[column].OFFERED_CAPACITY;
  }
  if (params.node && params.node.allLeafChildren) {
    return params.node.allLeafChildren[0].data[column].OFFERED_CAPACITY;
  }

  if (params.node) {
    return params.node.data.VOLUME;
  }
  return '';
}

export function getGroupColumnSameObjectForActions(params: any, column: string): any {
  if (params.data && params.data[column]) {
    return params.data[column].NEGOTIATION_STATUS;
  }
  if (params.node && params.node.allLeafChildren) {
    return params.node.allLeafChildren[0].data[column].NEGOTIATION_STATUS;
  }
  if (params.node) {
    return params.node.data.NEGOTIATION_STATUS;
  }
  return '';
}

export function getGroupColumnSameObjectForProjectName(params: any, column: string): any {
  if (params.data && params.data[column]) {
    return params.data[column].NAME;
  }
  if (params.node && params.node.allLeafChildren) {
    return params.node.allLeafChildren[0].data[column].NAME;
  }
  if (params.node) {
    return params.node.data.NAME;
  }
  return '';
}

export function isCheckboxVisible(params: any, isHeader: boolean): boolean {
  if ((isHeader && !params.data) || (!isHeader && params.data)) {
    return true;
  }
  return false;
}

export function filterNumericRange([fv1, fv2], cellValue: number[]): boolean {
  const lowerBound = cellValue[0];
  const upperBound = cellValue[1];

  return lowerBound >= fv1 && upperBound <= fv2;
}

export function filterLessOrEqualNumeric(fv1, cellValue: number[]): boolean {
  const upperBound = cellValue[1];
  return fv1 >= upperBound;
}

export function filterGreaterOrEqualNumeric(fv1, cellValue: number[]): boolean {
  const lowerBound = cellValue[0];
  return fv1 <= lowerBound;
}

export function filterEqualNumeric(fv1, cellValue: number[]): boolean {
  const lowerBound = cellValue[0];
  const upperBound = cellValue[1];
  return fv1 == lowerBound || fv1 == upperBound;
}

export function compareVPPAValues(
  valueA: any,
  valueB: any,
  nodeA: any,
  nodeB: any,
  vppaProperty: string,
  dataProperty: string,
) {
  let valA = valueA;
  let valB = valueB;

  if (nodeA) {
    if (nodeA.data) {
      valA = nodeA.data.VPPA_OBJECT
        ? nodeA.data.VPPA_OBJECT[vppaProperty]
        : nodeA.data[dataProperty];
    } else {
      valA = nodeA.allLeafChildren[0].data.VPPA_OBJECT[vppaProperty];
    }
  }

  if (nodeB) {
    if (nodeB.data) {
      valB = nodeB.data.VPPA_OBJECT
        ? nodeB.data.VPPA_OBJECT[vppaProperty]
        : nodeB.data[dataProperty];
    } else {
      valB = nodeB.allLeafChildren[0].data.VPPA_OBJECT[vppaProperty];
    }
  }

  if (valA === valB) {
    return 0;
  } else {
    return valA > valB ? 1 : -1;
  }
}

export function compareRFQValues(
  valueA: any,
  valueB: any,
  nodeA: any,
  nodeB: any,
  rfqProperty: string,
  dataProperty: string,
) {
  let valA = valueA;
  let valB = valueB;
  if (nodeA) {
    if (nodeA.data) {
      valA = nodeA.data.RFQ_OBJECT ? nodeA.data.RFQ_OBJECT[rfqProperty] : nodeA.data[dataProperty];
    } else {
      valA = nodeA.allLeafChildren[0].data.RFQ_OBJECT[rfqProperty];
    }
  }

  if (nodeB) {
    if (nodeB.data) {
      valB = nodeB.data.RFQ_OBJECT ? nodeB.data.RFQ_OBJECT[rfqProperty] : nodeB.data[dataProperty];
    } else {
      valB = nodeB.allLeafChildren[0].data.RFQ_OBJECT[rfqProperty];
    }
  }

  if (valA === valB) {
    return 0;
  } else {
    return valA > valB ? 1 : -1;
  }
}

// For settlement point, we want to check the contract type first and use a different value for the comparison
export function compareSettlementPointValues(
  nodeA: any,
  nodeB: any,
  contractProperty: string,
  settlementPointProperty: string,
) {
  let valA: string;
  let valB: string;

  if (nodeA) {
    if (nodeA.data) {
      // On Seller/Buyer Side - no grouping - normal rows just have data
      valA =
          nodeA.data[contractProperty] === CONTRACT_TYPE.REC
              ? ''
              : nodeA.data[settlementPointProperty];
    } else {
      // For grouped rows in Trading Activity Tab
      if (nodeA.allLeafChildren[0].data.RFQ_OBJECT) {
        // On the Buyer Side - with grouping - the parent RFQ row will have children and the children will reference the original RFQ_OBJECT
        valA =
          nodeA.allLeafChildren[0].data.RFQ_OBJECT[contractProperty] === CONTRACT_TYPE.REC
            ? ''
            : nodeA.allLeafChildren[0].data.RFQ_OBJECT[settlementPointProperty];
      } else if (nodeA.allLeafChildren[0].data.VPPA_OBJECT) {
        // On the Seller Side - with grouping - the parent Contract row will have children and the children will reference the original VPPA_OBJECT
        valA =
          nodeA.allLeafChildren[0].data.VPPA_OBJECT[contractProperty] === CONTRACT_TYPE.REC
            ? ''
            : nodeA.allLeafChildren[0].data.VPPA_OBJECT[settlementPointProperty];
      }
    }
  }

  // Same logic for node B
  if (nodeB) {
    if (nodeB.data) {
      valB =
          nodeB.data[contractProperty] === CONTRACT_TYPE.REC
              ? ''
              : nodeB.data[settlementPointProperty];
    }
    else {
      if (nodeB.allLeafChildren[0].data.RFQ_OBJECT) {
        valB =
            nodeB.allLeafChildren[0].data.RFQ_OBJECT[contractProperty] === CONTRACT_TYPE.REC
                ? ''
                : nodeB.allLeafChildren[0].data.RFQ_OBJECT[settlementPointProperty];
      } else if (nodeB.allLeafChildren[0].data.VPPA_OBJECT) {
        valB =
            nodeB.allLeafChildren[0].data.VPPA_OBJECT[contractProperty] === CONTRACT_TYPE.REC
                ? ''
                : nodeB.allLeafChildren[0].data.VPPA_OBJECT[settlementPointProperty];
      }
    }
  }

  if (valA === valB) {
    return 0;
  } else {
    return valA > valB ? 1 : -1;
  }
}

// AG Grid Default Column Header Template (with added info icon after the column header)
// Source: https://www.ag-grid.com/javascript-data-grid/column-headers/#provided-component
const HeaderWithTooltipTemplate =
  '<div class="ag-cell-label-container" role="presentation">' +
  '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
  '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
  '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
  '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
  '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
  '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
  `    <span ref="eText" class="ag-header-cell-text" role="columnheader"></span> <div style="margin-left: 4px" width="16px" height="16px">${infoIcon('start', '14px', '14px')}</div> ` +
  '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
  '  </div>' +
  '</div>';

export const CUSTOM_HEADER_TOOLTIPS = {
  CREDIT_PROFILE: CreditProfileLegendTooltipComponent,
  ICON_LEGEND: IconLegendTooltipComponent,
};

/** For the Custom Header with a Tooltip Icon
 *
 * @Note Need to set headerTooltip to something. It will not appear if an empty string.
 * @note headerComponentParams: use header template, which adds the info icon to the header
 *
 * @param tooltipComponent any set to our component to render our custom tooltip
 */

export function headerWithTooltipColParams(tooltipComponent?: any) {
  return {
    headerTooltip: 'lorem ipsum',
    headerComponentParams: {
      template: HeaderWithTooltipTemplate,
    },
    tooltipComponent: tooltipComponent,
  };
}

/**
 * Function to determine if the grid should autosize based on the state key
 *
 * If the gridOptions doesn't exist, should autosize
 * If the state key doesn't exist, should autosize
 * If the state key exists, should autosize if all columns have a width of 200 (default ag grid column width)
 * @Note: Ag grid default column width is 200
 * @Note: If the first column is the column to expand and collapse groups, then we want to ignore than column in our check
 *
 * @param stateKey string
 */
export function shouldAutosizeByStateKey(stateKey: string): boolean {
  if (localStorage.getItem(LOCAL_STORAGE_GRID_KEY)) {
    const gridOptions = JSON.parse(localStorage.getItem(LOCAL_STORAGE_GRID_KEY));
    if (gridOptions && stateKey in gridOptions) {
      const savedState = JSON.parse(gridOptions[stateKey]);
      if (savedState[0].colId === '0') {
        // ignore the first column's width because the width is hard coded
        savedState.shift();
      }
      // Note: If ag grid every changes this value then we need to manually update
      return savedState.every((col) => col.width === 200);
    } else {
      // Grid options exist but this tab is new
      return true;
    }
  }
  return true;
}
